type ImageOptimizationOptions = {
  width?: number;
  height?: number;
  quality?: string;
  fit?: 'limit' | 'fill' | 'crop' | 'scale';
};

const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined;
const SITE_ORIGIN = (import.meta.env.VITE_SITE_ORIGIN as string | undefined)?.replace(/\/+$/, '');

function normalizeSourceUrl(src: string): string {
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }

  const cleanSrc = src.startsWith('/') ? src : `/${src}`;
  if (SITE_ORIGIN) return `${SITE_ORIGIN}${cleanSrc}`;
  if (typeof window !== 'undefined') return `${window.location.origin}${cleanSrc}`;
  return cleanSrc;
}

export function getOptimizedImageUrl(
  src: string,
  options: ImageOptimizationOptions = {},
): string {
  if (!src) return src;
  if (!CLOUDINARY_CLOUD_NAME) return src;

  const transforms = [
    'f_auto',
    'dpr_auto',
    `q_${options.quality ?? 'auto:good'}`,
    `c_${options.fit ?? 'limit'}`,
    options.width ? `w_${options.width}` : null,
    options.height ? `h_${options.height}` : null,
  ]
    .filter(Boolean)
    .join(',');

  const sourceUrl = normalizeSourceUrl(src);
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/fetch/${transforms}/${encodeURIComponent(sourceUrl)}`;
}

export function getOptimizedImageSrcSet(
  src: string,
  widths: number[],
  options: Omit<ImageOptimizationOptions, 'width'> = {},
): string | undefined {
  if (!src || !CLOUDINARY_CLOUD_NAME || widths.length === 0) return undefined;

  const uniqueSortedWidths = Array.from(new Set(widths))
    .filter((width) => Number.isFinite(width) && width > 0)
    .sort((a, b) => a - b);

  if (uniqueSortedWidths.length === 0) return undefined;

  return uniqueSortedWidths
    .map((width) => `${getOptimizedImageUrl(src, { ...options, width })} ${width}w`)
    .join(', ');
}
